import React, { useEffect, useState, useCallback } from 'react';
import { workflowAPI, settingsAPI } from '../api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { DndContext, DragOverlay, useDraggable, useDroppable, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { Plus, Trash2, Save, X, Settings2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const COLORS = ['#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981', '#06B6D4', '#14B8A6', '#0EA5E9', '#84CC16', '#22C55E', '#F97316', '#DC2626', '#16A34A', '#065F46', '#059669', '#6B7280'];
const ROLES = ['', 'sales_executive', 'manager', 'architect', 'site_engineer', 'accountant'];

const LeadChip = ({ lead, onClick }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: lead.id });
  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, opacity: isDragging ? 0.5 : 1 } : undefined;
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}
      data-testid={`wf-card-${lead.id}`}
      onClick={onClick}
      className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-3 shadow-sm cursor-move hover:shadow-md mb-2">
      <p className="font-medium text-sm truncate">{lead.customer_name}</p>
      <p className="text-xs text-muted-foreground">{lead.primary_mobile}</p>
      {lead.workflow_progress != null && (
        <div className="mt-2">
          <div className="h-1 bg-slate-100 dark:bg-slate-700 rounded-full">
            <div className="h-1 bg-primary rounded-full" style={{ width: `${lead.workflow_progress}%` }} />
          </div>
          <p className="text-[10px] text-muted-foreground mt-1">{lead.workflow_progress}%</p>
        </div>
      )}
    </div>
  );
};

const Column = ({ stage, onCardClick }) => {
  const { isOver, setNodeRef } = useDroppable({ id: stage.id });
  return (
    <div ref={setNodeRef} data-testid={`wf-col-${stage.id}`}
      className={`flex-shrink-0 w-72 bg-slate-50 dark:bg-slate-900/50 rounded-lg p-3 ${isOver ? 'ring-2 ring-primary' : ''}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: stage.color }}></div>
          <h3 className="font-medium text-sm truncate">{stage.name}</h3>
        </div>
        <span className="text-xs bg-white dark:bg-slate-800 px-2 py-0.5 rounded-full">{stage.count}</span>
      </div>
      {stage.role_required && <p className="text-[10px] text-muted-foreground mb-2 capitalize">{stage.role_required.replace('_', ' ')} only</p>}
      <div className="space-y-2 min-h-[80px] max-h-[calc(100vh-320px)] overflow-y-auto">
        {stage.leads.map(l => <LeadChip key={l.id} lead={l} onClick={() => onCardClick(l.id)} />)}
      </div>
    </div>
  );
};

const StageEditor = ({ serviceId, serviceName, onClose, onChange }) => {
  const [stages, setStages] = useState([]);
  const [form, setForm] = useState({ name: '', color: COLORS[0], role_required: '' });

  const load = useCallback(async () => {
    const { data } = await workflowAPI.listStages(serviceId);
    setStages(data);
  }, [serviceId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.name.trim()) return toast.error('Name required');
    try {
      await workflowAPI.createStage({
        service_id: serviceId,
        name: form.name,
        color: form.color,
        role_required: form.role_required || null,
        display_order: stages.length,
        is_active: true
      });
      setForm({ name: '', color: COLORS[0], role_required: '' });
      load(); onChange();
      toast.success('Stage added');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this stage?')) return;
    try { await workflowAPI.deleteStage(id); load(); onChange(); toast.success('Deleted'); }
    catch { toast.error('Failed'); }
  };

  const move = async (idx, dir) => {
    const arr = [...stages]; const s = idx + dir;
    if (s < 0 || s >= arr.length) return;
    [arr[idx], arr[s]] = [arr[s], arr[idx]];
    setStages(arr);
    try { await workflowAPI.reorderStages(arr.map(x => x._id || x.id)); onChange(); }
    catch { toast.error('Reorder failed'); load(); }
  };

  const toggle = async (s) => {
    try { await workflowAPI.updateStage(s._id || s.id, { is_active: !s.is_active }); load(); onChange(); }
    catch { toast.error('Failed'); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-[#131A2B] rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-outfit font-semibold">Edit Workflow — {serviceName}</h2>
            <p className="text-xs text-muted-foreground">Add, reorder, color-code, and gate stages by role</p>
          </div>
          <button onClick={onClose}><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex flex-wrap gap-2 items-center border border-slate-200 dark:border-slate-700 rounded-lg p-3">
            <input type="text" placeholder="Stage name" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="wf-new-stage-name"
              className="flex-1 min-w-[160px] px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm" />
            <select value={form.role_required} onChange={(e) => setForm({ ...form, role_required: e.target.value })}
              className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm">
              {ROLES.map(r => <option key={r} value={r}>{r ? r.replace('_', ' ') : 'Any role'}</option>)}
            </select>
            <div className="flex gap-1 flex-wrap">
              {COLORS.slice(0, 8).map(c => (
                <button key={c} onClick={() => setForm({ ...form, color: c })}
                  className={`w-5 h-5 rounded ${form.color === c ? 'ring-2 ring-primary ring-offset-1' : ''}`}
                  style={{ backgroundColor: c }} />
              ))}
            </div>
            <button onClick={add} data-testid="wf-add-stage-btn" className="inline-flex items-center gap-1 px-3 py-2 bg-primary text-white rounded-lg text-sm">
              <Plus className="h-4 w-4" /> Add
            </button>
          </div>

          <div className="space-y-2">
            {stages.map((s, idx) => {
              const sid = s._id || s.id;
              return (
                <div key={sid} className="flex items-center gap-2 p-2 border border-slate-200 dark:border-slate-700 rounded-lg" data-testid={`wf-stage-${sid}`}>
                  <div className="flex flex-col text-xs">
                    <button onClick={() => move(idx, -1)} disabled={idx === 0} className="disabled:opacity-30">▲</button>
                    <button onClick={() => move(idx, 1)} disabled={idx === stages.length - 1} className="disabled:opacity-30">▼</button>
                  </div>
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color }}></div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{s.name}</p>
                    {s.role_required && <p className="text-xs text-muted-foreground capitalize">Requires: {s.role_required.replace('_', ' ')}</p>}
                  </div>
                  <button onClick={() => toggle(s)} className={`text-xs px-2 py-0.5 rounded ${s.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                    {s.is_active ? 'Active' : 'Disabled'}
                  </button>
                  <button onClick={() => remove(sid)} className="p-1.5 bg-red-50 text-red-600 rounded" data-testid={`wf-del-stage-${sid}`}>
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export const ProjectWorkflowPage = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [services, setServices] = useState([]);
  const [activeService, setActiveService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [editingService, setEditingService] = useState(null);
  const navigate = useNavigate();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await workflowAPI.byService();
      setServices(data || []);
      if (data?.length && !activeService) setActiveService(data[0].service_id);
    } catch { toast.error('Failed to load workflows'); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const current = services.find(s => s.service_id === activeService);

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over || !current) return;
    const stageId = over.id;
    const leadId = active.id;
    // Find current lead
    let lead = null;
    for (const s of current.stages) { const l = s.leads.find(x => x.id === leadId); if (l) { lead = l; break; } }
    if (!lead) return;
    try {
      await workflowAPI.move(leadId, stageId);
      toast.success('Stage updated');
      fetch();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Move failed');
      fetch();
    }
  };

  const activeLead = activeId && current
    ? current.stages.flatMap(s => s.leads).find(l => l.id === activeId)
    : null;

  return (
    <div className="space-y-6" data-testid="workflow-container">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-outfit font-semibold">Project Workflows</h1>
          <p className="text-muted-foreground">Service-based dynamic pipelines with role gating</p>
        </div>
        {isSuperAdmin && current && (
          <button onClick={() => setEditingService({ id: current.service_id, name: current.service_name })}
            data-testid="wf-edit-stages-btn"
            className="inline-flex items-center gap-2 px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg text-sm">
            <Settings2 className="h-4 w-4" /> Edit Stages
          </button>
        )}
      </div>

      {loading ? <div className="flex items-center justify-center h-40"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div></div> :
       services.length === 0 ? <div className="clay-card p-8 text-center text-sm text-muted-foreground">No services yet.</div> :
       <>
         <div className="flex gap-2 overflow-x-auto pb-2" data-testid="wf-service-tabs">
           {services.map(s => (
             <button key={s.service_id} onClick={() => setActiveService(s.service_id)}
               data-testid={`wf-service-${s.service_name}`}
               className={`px-4 py-2 rounded-lg text-sm whitespace-nowrap transition-colors ${activeService === s.service_id ? 'bg-primary text-white' : 'bg-slate-100 dark:bg-slate-800 hover:bg-slate-200'}`}
               style={activeService === s.service_id ? { backgroundColor: s.color } : {}}>
               {s.service_name}
             </button>
           ))}
         </div>

         {current && (
           <DndContext sensors={sensors} onDragStart={(e) => setActiveId(e.active.id)} onDragEnd={handleDragEnd}>
             <div className="flex gap-3 overflow-x-auto pb-4">
               {current.stages.length === 0 ? (
                 <div className="clay-card p-8 text-center text-sm text-muted-foreground flex-1">
                   No stages defined yet. {isSuperAdmin && 'Click "Edit Stages" to add stages.'}
                 </div>
               ) : current.stages.map(stage => (
                 <Column key={stage.id} stage={stage} onCardClick={(id) => navigate(`/leads/${id}`)} />
               ))}
             </div>
             <DragOverlay>
               {activeLead && <LeadChip lead={activeLead} onClick={() => {}} />}
             </DragOverlay>
           </DndContext>
         )}
       </>}

      {editingService && (
        <StageEditor serviceId={editingService.id} serviceName={editingService.name}
          onClose={() => setEditingService(null)}
          onChange={fetch} />
      )}
    </div>
  );
};
export default ProjectWorkflowPage;
